import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';

import { Button } from '@domcrm/embed-ui';

import { useEmbedBootstrap } from '../use-widget-bootstrap';
import { useQuerySuffix } from '../widget-hooks/useQuerySuffix';
import { PATH } from '../widget-lib/path-routes';
import { EmbedMessageBlock } from '../widget-ui/embed-message-block';
import GlobalWidgetAwaitingSignedUrlPage from './global_GlobalWidgetAwaitingSignedUrlPage';
import GlobalWidgetLandingPage from './global_GlobalWidgetLandingPage';
import HomePage from './landing_HomePage';
import { ClientIndexSkeleton } from './landing_ClientIndexSkeleton';

function parseGuestOrgId(raw: string | null): number | null {
  if (!raw?.trim()) return null;
  const n = parseInt(raw.trim(), 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Корень клиентского embed: глобальный виджет без сессии — стартовый экран без принудительного входа (DoD: гостевой просмотр до авторизации для записи/оплаты).
 * Витрина: выбор org по `guest_org_id` без подписанной ссылки на студию.
 */
export default function ClientIndexPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const embedGlobal = searchParams.get('embed_global') === '1';
  const guestOrgIdParam = parseGuestOrgId(searchParams.get('guest_org_id'));
  const {
    accessToken,
    canFetchWidget,
    widgetConfig,
    widgetContour,
    loading,
    error,
    orgSlug,
    guestShowcaseOrg,
    guestShowcaseLoading,
    guestShowcaseError,
    guestShowcaseBrowse,
    reload
  } = useEmbedBootstrap();
  const suffix = useQuerySuffix();

  if (widgetContour === 'booking' && (orgSlug || guestOrgIdParam != null)) {
    return <Navigate to={`${PATH.schedule}${suffix}`} replace />;
  }

  if ((widgetContour === 'trainer' || widgetContour === 'trainer-mobile') && !accessToken) {
    return <Navigate to={`${PATH.trainerSignIn}${suffix}`} replace />;
  }

  if (embedGlobal && !orgSlug && guestOrgIdParam == null) {
    return <GlobalWidgetLandingPage />;
  }

  if (!accessToken) {
    if (embedGlobal && !orgSlug && guestOrgIdParam == null) {
      return <GlobalWidgetLandingPage />;
    }

    if (guestOrgIdParam != null && guestShowcaseLoading) {
      return <ClientIndexSkeleton />;
    }

    if (guestOrgIdParam != null && guestShowcaseError) {
      return (
        <div className="w-full min-w-0 space-y-3">
          <EmbedMessageBlock variant="error" size="field" role="alert">
            {guestShowcaseError}
          </EmbedMessageBlock>
          <Button type="button" className="!h-9" onClick={() => reload()}>
            Повторить
          </Button>
        </div>
      );
    }

    // Витрина: guest_org_id указан, но студии больше нет в каталоге — мягкая заглушка вместо ошибки.
    if (embedGlobal && guestOrgIdParam != null && !guestShowcaseLoading && !guestShowcaseOrg) {
      return (
        <div className="w-full min-w-0 space-y-3">
          <EmbedMessageBlock variant="info" size="field" role="status">
            Студия больше не доступна в каталоге.
          </EmbedMessageBlock>
          <Button
            type="button"
            className="!h-9"
            onClick={() => {
              const next = new URLSearchParams(searchParams);
              next.delete('guest_org_id');
              navigate({ pathname: PATH.home, search: next.toString() ? `?${next.toString()}` : '' });
            }}
          >
            Выбрать другую студию
          </Button>
        </div>
      );
    }

    if (canFetchWidget && loading) {
      return <ClientIndexSkeleton />;
    }
    if (canFetchWidget && !loading && error) {
      return (
        <div className="w-full min-w-0 space-y-3">
          <EmbedMessageBlock variant="error" size="field" role="alert">
            {error}
          </EmbedMessageBlock>
          <Button type="button" className="!h-9" onClick={() => reload()}>
            Повторить
          </Button>
        </div>
      );
    }

    // org_slug без подписанной ссылки и без гостевого доступа — «нужна подписанная ссылка» / вход.
    if (orgSlug && !canFetchWidget && !guestShowcaseOrg && !guestShowcaseLoading) {
      return <GlobalWidgetAwaitingSignedUrlPage />;
    }
  }

  const widgetSingleBranch = Boolean(canFetchWidget && !accessToken && widgetConfig?.branches?.length === 1);
  const guestSingleBranch = Boolean(
    guestShowcaseBrowse && guestShowcaseOrg?.branches && guestShowcaseOrg.branches.length <= 1
  );

  if (canFetchWidget && !accessToken && loading) {
    return <ClientIndexSkeleton />;
  }

  if (widgetSingleBranch || guestSingleBranch) {
    return <Navigate to={`${PATH.schedule}${suffix}`} replace />;
  }

  return <HomePage />;
}
